import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Shield } from 'lucide-react';
import { InlineLoader } from './Loader';
import { getCachedBranding } from '../contexts/BrandingContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const DEFAULT_LOGIN_BRANDING = {
  companyName: 'Smart Installers',
  logoUrl: '/l_white.png',
  subtitle: 'Device Monitoring Platform',
  logoBgColor: '#164da6',
  logoScale: 'medium'
};

const Login = ({ onLoginSuccess }) => {
  const { loginWithRedirect, isLoading, isAuthenticated } = useAuth0();

  const [branding, setBranding] = useState(() => getCachedBranding() || DEFAULT_LOGIN_BRANDING);

  useEffect(() => {
    const fetchPublicBranding = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const orgSlug = urlParams.get('org');

        const url = orgSlug
          ? `${API_BASE_URL}/public/branding/?org=${encodeURIComponent(orgSlug)}`
          : `${API_BASE_URL}/public/branding/`;

        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          if (data.is_white_labeled) {
            setBranding({
              companyName: data.company_name,
              logoUrl: data.logo_url,
              subtitle: data.subtitle || 'Device Monitoring Platform',
              logoBgColor: data.logo_bg_color || '#164da6',
              logoScale: data.logo_scale || 'medium'
            });
          }
        }
      } catch (error) {
        console.warn('Failed to fetch public branding, using defaults');
      }
    };

    fetchPublicBranding();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      onLoginSuccess();
    }
  }, [isAuthenticated, onLoginSuccess]);

  const handleLogin = () => {
    loginWithRedirect();
  };

  const logoSizeMap = {
    small: { container: 48, image: 36 },
    medium: { container: 64, image: 48 },
    large: { container: 80, image: 64 },
    xlarge: { container: 96, image: 80 },
    xxlarge: { container: 112, image: 96 }
  };
  const logoSize = logoSizeMap[branding.logoScale] || logoSizeMap.medium;
  const isTransparent = branding.logoBgColor === 'transparent';

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo-icon">
            <img src={branding.logoUrl} alt={`${branding.companyName} Logo`} className="login-logo-image" />
          </div>
          <h1 className="login-title">{branding.companyName}</h1>
          <p className="login-subtitle">{branding.subtitle}</p>
        </div>

        <div className="login-form">
          <button
            type="button"
            onClick={handleLogin}
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <InlineLoader size="small" />
                <span>Connecting...</span>
              </>
            ) : (
              <span>Sign In Securely</span>
            )}
          </button>
        </div>

        <div className="login-footer">
          <Shield size={14} strokeWidth={2} aria-hidden="true" />
          <span>Secured by Auth0</span>
        </div>
      </div>

      <style jsx>{`
        .login-container {
          flex: 1;
          min-height: 100vh;
          background: #f5f6f8;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
        }

        .login-card {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 2.5rem 2rem;
          width: 100%;
          max-width: 400px;
          box-shadow:
            0 1px 3px rgba(0, 0, 0, 0.04),
            0 1px 2px rgba(0, 0, 0, 0.03);
        }

        .login-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .login-logo-icon {
          width: ${logoSize.container}px;
          height: ${logoSize.container}px;
          background: ${isTransparent ? 'transparent' : branding.logoBgColor};
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
          overflow: hidden;
        }

        .login-logo-image {
          width: ${logoSize.image}px;
          height: ${logoSize.image}px;
          object-fit: contain;
        }

        .login-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: #1a1a1a;
          letter-spacing: -0.025em;
          margin: 0 0 0.5rem;
        }

        .login-subtitle {
          font-size: 0.875rem;
          font-weight: 400;
          color: #4a5568;
          margin: 0;
        }

        .login-form {
          margin-bottom: 1.5rem;
        }

        .login-button {
          width: 100%;
          min-height: 44px;
          padding: 0.75rem 1.5rem;
          background: #164da6;
          color: #ffffff;
          border: none;
          border-radius: 8px;
          font-family: inherit;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          transition: background 0.15s ease;
        }

        .login-button:hover:not(:disabled) {
          background: #1242b3;
        }

        .login-button:focus-visible {
          outline: none;
          box-shadow: 0 0 0 3px rgba(22, 77, 166, 0.25);
        }

        .login-button:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }

        .login-footer {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.375rem;
          color: #9ca3af;
          font-size: 0.75rem;
          font-weight: 400;
        }

        @media (max-width: 480px) {
          .login-card {
            padding: 2rem 1.5rem;
          }
          .login-title {
            font-size: 1.375rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Login;
